<script setup lang="ts">
import { computed, ref } from 'vue'

import { Trash2 } from '@lucide/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouteParams } from '@vueuse/router'
import { useRouter } from 'vue-router'

import SkillForm, { type SkillFormValues } from '#content/components/SkillForm.vue'
import DetailState from '#shared/components/DetailState.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import ProductionConfirm from '#shared/components/ProductionConfirm.vue'
import { contextHubClient } from '#shared/services/api/context-hub.client'
import type { components } from '#shared/services/api/context-hub.schema'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '#ui/alert-dialog'
import { Button } from '#ui/button'
import { CONTENT_MOTION } from '#utils/motion'

type SkillDto = components['schemas']['SkillDto']

definePage({
  meta: { requiresAuth: true, title: 'Skill' },
})

const router = useRouter()

const id = useRouteParams<string>('skillId')

const queryClient = useQueryClient()

const skillKey = computed(() => ['skills', id.value] as const)

const skillQuery = useQuery({
  queryKey: skillKey,
  queryFn: async (): Promise<SkillDto> => {
    const { data, error } = await contextHubClient.GET('/api/admin/skills/{id}', {
      params: { path: { id: id.value } },
    })
    if (error) throw error
    return data
  },
})

const updateMetadata = useMutation({
  mutationFn: async (body: components['schemas']['UpdateSkillMetadataRequest']) => {
    const r = await contextHubClient.PUT('/api/admin/skills/{id}', {
      params: { path: { id: id.value } },
      body,
    })
    return r.data
  },
  meta: { success: 'Skill saved.', error: 'Failed to save skill.' },
})

const updateContent = useMutation({
  mutationFn: async (body: components['schemas']['UpdateSkillContentRequest']) => {
    const r = await contextHubClient.PUT('/api/admin/skills/{id}/content', {
      params: { path: { id: id.value } },
      body,
    })
    return r.data
  },
  meta: { success: 'Skill content saved.', error: 'Failed to save skill content.' },
})

const deleteSkill = useMutation({
  mutationFn: async () => {
    await contextHubClient.DELETE('/api/admin/skills/{id}', {
      params: { path: { id: id.value } },
    })
  },
  meta: { success: 'Skill deleted.', error: 'Failed to delete skill.' },
})

const toggleEnabledMutation = useMutation({
  mutationFn: async (enable: boolean) => {
    const url = enable ? '/api/admin/skills/{id}/enable' : '/api/admin/skills/{id}/disable'
    await contextHubClient.POST(url, { params: { path: { id: id.value } } })
  },
  onMutate: async (enable: boolean) => {
    await queryClient.cancelQueries({ queryKey: skillKey.value })
    const previous = queryClient.getQueryData<SkillDto>(skillKey.value)
    if (previous)
      queryClient.setQueryData<SkillDto>(skillKey.value, { ...previous, isEnabled: enable })
    return { previous }
  },
  onError: (_error, _enable, context) => {
    if (context?.previous) queryClient.setQueryData(skillKey.value, context.previous)
  },
  meta: { error: 'Failed to change skill status.' },
})

const initial = computed<Partial<SkillFormValues> | undefined>(() => {
  const s = skillQuery.data.value
  if (!s) return undefined
  return {
    name: s.name ?? '',
    description: s.description ?? '',
    userFacingIntent: s.userFacingIntent ?? '',
    content: s.activeVersion?.content ?? '',
    tags: s.tags ?? [],
    organizationScope: s.organizationScope ?? 'Global',
    organizationIds: s.organizationIds ?? [],
    runMode: s.runMode ?? 'MainAgent',
  }
})

const deleteOpen = ref(false)
const saving = computed(() => updateMetadata.isPending.value || updateContent.isPending.value)

async function handleSubmit(values: SkillFormValues) {
  await updateMetadata.mutateAsync({
    name: values.name,
    description: values.description,
    userFacingIntent: values.userFacingIntent,
    tags: values.tags,
    organizationScope: values.organizationScope,
    organizationIds: values.organizationScope === 'Restricted' ? values.organizationIds : [],
    runMode: values.runMode,
  })
  const currentContent = skillQuery.data.value?.activeVersion?.content ?? ''
  if (values.content !== currentContent) {
    await updateContent.mutateAsync({ content: values.content })
  }
  router.push('/skills')
}

function handleCancel() {
  router.push('/skills')
}

async function handleDelete() {
  await deleteSkill.mutateAsync()
  deleteOpen.value = false
  router.push('/skills')
}

async function toggleEnabled() {
  if (!skillQuery.data.value) return
  await toggleEnabledMutation.mutateAsync(!skillQuery.data.value.isEnabled)
}
const deleteConfirmed = ref(true)
</script>

<template>
  <PageHeader :title="skillQuery.data.value?.name || 'Edit skill'">
    <template #actions>
      <Button
        v-if="skillQuery.data.value"
        variant="outline"
        :disabled="toggleEnabledMutation.isPending.value"
        @click="toggleEnabled"
      >
        {{ skillQuery.data.value.isEnabled ? 'Disable' : 'Enable' }}
      </Button>
      <AlertDialog v-model:open="deleteOpen">
        <AlertDialogTrigger as-child>
          <Button variant="outline" :disabled="skillQuery.isLoading.value">
            <Trash2 />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this skill?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the skill from the Context Hub. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ProductionConfirm v-model:confirmed="deleteConfirmed" phrase="delete skill" />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              :disabled="deleteSkill.isPending.value || !deleteConfirmed"
              @click="handleDelete"
            >
              {{ deleteSkill.isPending.value ? 'Deleting…' : 'Delete' }}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </template>
  </PageHeader>

  <Transition v-bind="CONTENT_MOTION">
    <DetailState
      v-if="skillQuery.isLoading.value || skillQuery.error.value"
      :is-loading="skillQuery.isLoading.value"
      :error="skillQuery.error.value"
      noun="skill"
    />

    <SkillForm
      v-else-if="initial"
      :initial="initial"
      :submitting="saving"
      submit-label="Save changes"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
  </Transition>
</template>
